import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AxiosRequestConfig } from "axios";
import { Spinner } from "components";
import { Check, CircleX, Search } from "lucide-react";
import { editGenericOrder } from "pages/GenericOrderDetailsPage/apis";
import React, { useEffect, useRef, useState } from "react";
import {
  GetLinkLeadListRequestType,
  getLinkLeadGenericSearchList,
} from "service/apis/DealDetails";
import { toast } from "sonner";

interface LinkPartnerProps {
  open: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;

  readonly onDone?: (partner: Partner) => void;

  readonly otherProps?: any;
  readonly showLinkPartnerButton?: boolean;
  readonly isSetCancellationCharges?: boolean;
  readonly DealDetails?: () => void;
  readonly setIsSetCancellationCharges?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}


export interface Partner {
  id: string;
  name: string;
  email: string;
  role: string;
  unique_id: any;
  productName: any;
  CustomerName: any;
  CustomerConatct: any;
  isCoustomer: any;
}

export default function LinkLeadDialog({
  open,
  onOpenChange,
  onDone,
  otherProps,
  DealDetails,
}: LinkPartnerProps) {
  console.log("otherProps in link lead dialog", otherProps);
  const [value, setValue] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [allPartners, setAllPartners] = useState<Partner[]>([]);
  const [filteredPartners, setFilteredPartners] = useState<Partner[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function useDebounce(
    callback: () => void,
    delay: number,
    dependencies: any[],
  ) {
    useEffect(() => {
      if (!dependencies.some((dep) => dep === "")) {
        const handler = setTimeout(callback, delay);
        return () => clearTimeout(handler);
      }
    }, [...dependencies, delay]);
  }

  // search contact number
  const fetchSearchResults = async () => {
    if (!value.trim()) return;

    const auth = localStorage.getItem("authToken");
    if (!auth) return;

    const authToken = JSON.parse(auth);

    const payload: AxiosRequestConfig<GetLinkLeadListRequestType> = {

      params: {
        searchQuery: value,
        purpose_id: otherProps?.purposeID,
      },
    };

    try {
      setLoading(true);

      const response = await getLinkLeadGenericSearchList(payload);

      const partners =
        response?.data?.map((partner: any) => ({
          id: partner.id.toString(),
          name: partner?.customers?.full_name || "N/A",
          productName:
            partner?.product_details?.length
              ? partner.product_details.map((p: any) => p.name).join(", ")
              : partner?.product_name?.name || "N/A",

          CustomerName: partner?.customers?.full_name || "N/A",
          CustomerConatct: partner?.customers?.phone_number || "",
          isCoustomer: partner?.customers?.customer_types?.includes("Customer"),
          role: partner.status,
          unique_id: partner.unique_id,
        })) || [];

      setFilteredPartners(partners);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };


 
  useDebounce(
    () => {
      if (selectedPartner) return; // ✅ prevent reopening

      if (value.trim()) {
        setIsDropdownOpen(true);
        fetchSearchResults();
      } else {
        setIsDropdownOpen(false);
        setFilteredPartners([]);
      }
    },
    500,
    [value, selectedPartner],
  );



  const handlePartnerSelect = (partner: Partner) => {
    setSelectedPartner(partner);
    setValue(partner.name);
    setIsDropdownOpen(false); 
  };



  const removeSelectedPartner = () => {
    setSelectedPartner(null);
    setValue("");
  };

  const handleDoneClick = async () => {
    if (!selectedPartner?.id) {
      toast.error("Please select a lead");
      return;
    }

    if (!otherProps) {
      toast.error("Invalid order");
      return;
    }

    try {
      await editGenericOrder({
        order_id: otherProps,
        lead_id: selectedPartner.id,
      });

      toast.success("Lead linked successfully!");

      DealDetails?.();        // ✅ refetch order details
      onDone?.(selectedPartner);
      onOpenChange(false);    // close dialog
    } catch (err) {
      console.error(err);
      toast.error("Failed to link lead");
    }
  };





  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedPartner) return; // ✅ block typing

    setValue(e.target.value);
    setIsDropdownOpen(true);
  };



  return (
    <>

      <AlertDialog open={open} onOpenChange={onOpenChange}>

        <AlertDialogPortal>
          <AlertDialogOverlay className=" bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
          <AlertDialogContent className="bg-white-A700 rounded-lg p-0 max-w-2xl animate-contentShow">
            <AlertDialogHeader className="flex text-left gap-4 py-5 px-6">
              <AlertDialogTitle className="font-bold text-xl leading-7 tracking-[2%] text-black-label">
                Link Lead
              </AlertDialogTitle>
            </AlertDialogHeader>
            <div className="px-9 overflow-y-auto h-[500px]">
              <div className="space-y-6">
                <div className="">
                  <h2 className="text-sm font-medium leading-[16.94px] mb-4 text-gray-600">
                    Search Lead
                  </h2>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <Input
                      value={value}
                      onChange={handleInputChange}
                      disabled={!!selectedPartner?.id}
                      placeholder="Search by mobile number or lead ID"
                      className="pl-10 py-6 text-base bg-white rounded-[30px] border-gray-200"
                      type="text"
                    />
                    {isDropdownOpen &&  !selectedPartner &&(
                      <div className="absolute w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[300px] overflow-y-auto z-50">
                        {loading ? (
                          <div className="p-4 text-center text-sm text-gray-500">
                            Searching…
                          </div>
                        ) : (
                          <div className="p-0 relative">
                            {filteredPartners.length === 0 ? (
                              <div className="p-4 text-sm text-gray-500">
                                No results found
                              </div>
                            ) : (
                                  <div className="p-0 relative">
                                    {!selectedPartner &&
                                      filteredPartners?.map((partner: any) => (
                                        <button
                                          key={partner?.id}
                                          className="flex justify-between items-center w-full p-3 hover:bg-gray-100 cursor-pointer"
                                          onClick={() => handlePartnerSelect(partner)}
                                        >
                                          <div className="flex flex-col gap-2 text-[14px]">
                                            <div className="flex flex-row gap-2 items-center">
                                              <span className="font-medium text-left text-[#999999]">
                                                OD- {partner?.id} |{" "}
                                              </span>
                                              <span className="text-sm text-[14px] text-black-500 text-left">
                                                {partner?.productName}
                                              </span>
                                            </div>
                                            <div className="flex flex-row gap-2 items-center">
                                              <span className="font-medium text-[#999999] text-left">
                                                {partner?.CustomerName}
                                              </span>
                                              <span className="text-sm text-gray-500 text-left">
                                                {partner?.CustomerConatct}
                                              </span>
                                            </div>
                                          </div>

                                          <Label
                                            className="p-3 bg-[#E1FFE2] text-[#23AB28]"
                                            style={{ borderRadius: "10px", textAlign: "center" }}
                                          >
                                            {partner?.isCoustomer ? "Customer" : "Contact"}
                                          </Label>
                                        </button>
                                      ))}

                            
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {selectedPartner && (
                <div className="mt-4">
                  <div className="flex flex-row justify-between items-center">
                    <h3 className="text-sm text-gray-600 mb-2">You added</h3>
                    <div className="flex">
                      <button onClick={removeSelectedPartner}>
                        <CircleX className="h-4 w-4 mb-2 text-[#165FE3]" />
                      </button>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 relative">
                    <div className="">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col gap-2 text-[14px]">
                          <div className="flex flex-row gap-2 items-center">
                            <span className="font-medium text-left text-[#999999]">
                              OD-{selectedPartner?.id} |{" "}
                            </span>
                            <span className="text-sm text-[14px] text-black-500 text-left">
                              {selectedPartner?.productName}
                            </span>
                          </div>
                          <div className="flex flex-row gap-2 items-center">
                            <span className="font-medium text-[#999999] text-left">
                              {selectedPartner?.CustomerName}
                            </span>
                            <span className="w-[7px] h-[7px] bg-gray-400 rounded-full inline-block"></span>
                            <span className="text-sm text-gray-500 text-left">
                              {selectedPartner?.CustomerConatct}
                            </span>
                          </div>
                        </div>
                        <div
                          className={`flex items-center ${selectedPartner.role === "lead"
                              ? "text-yellow-600"
                              : "text-green-600"
                            }`}
                        >
                          {selectedPartner.role === "active" && (
                            <Check className="h-4 w-4 mr-1" />
                          )}
                          <div>
                            <Label
                              className="p-3 bg-[#E1FFE2] text-[#23AB28]"
                              style={{
                                borderRadius: "10px",
                                textAlign: "center",
                              }}
                            >
                              {" "}
                              {selectedPartner?.isCoustomer
                                ? "Customer"
                                : "Contact"}
                            </Label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <AlertDialogFooter className="flex items-center gap-2.5 py-2 px-10 flex-row rounded-b-lg bg-gray-button-gray-lite">
              <AlertDialogCancel className="m-0 rounded-lg border-blue-700 border py-[11px] px-4 text-blue-700">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className="rounded-lg border-blue-700 py-[11px] px-4 bg-blue-700 border text-white-A700"
                onClick={() => setConfirmOpen(true)}
                disabled={!selectedPartner?.id}
              >
                Done
              </AlertDialogAction>

            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogPortal>
      </AlertDialog>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogPortal>
          <AlertDialogOverlay className="bg-black-900 bg-opacity-60 fixed inset-0 animate-overlayShow" />

          <AlertDialogContent className="bg-white-A700 rounded-lg p-0 max-w-xl animate-contentShow">
            {/* 🔹 Header */}
            <AlertDialogHeader className="flex flex-col text-left gap-2 p-4">
              <AlertDialogTitle className="font-bold text-xl text-black-label">
                Link lead with order?
              </AlertDialogTitle>

              <p className="font-medium text-base text-[#7E7E7E]">
                Are you sure you want to link this lead with the order?
              </p>
            </AlertDialogHeader>

            {/* 🔹 Footer */}
            <AlertDialogFooter className="flex flex-row justify-start items-end gap-2.5 py-2 px-5 rounded-b-lg bg-gray-button-gray-lite">
              {/* ❌ NO */}
              <AlertDialogCancel asChild>
                <button
                  className="rounded-lg border border-blue-700 py-[11px] px-4 text-blue-700"
                  onClick={() => setConfirmOpen(false)}
                >
                  No
                </button>
              </AlertDialogCancel>

              {/* ✅ YES */}
              <AlertDialogAction asChild>
                <button
                  className="rounded-lg bg-blue-700 border border-blue-700 py-[11px] px-4 text-white-A700"
                  onClick={async () => {
                    setConfirmOpen(false);
                    await handleDoneClick(); // 🔥 actual submit
                  }}
                >
                  Yes, link lead
                </button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogPortal>
      </AlertDialog>

    </>
  );
}